import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Archive,
  ArchiveRestore,
  Check,
  ChevronDown,
  ChevronUp,
  CircleCheck,
  ClipboardList,
  Factory,
  FileText,
  FileUp,
  FolderInput,
  FolderOpen,
  GripVertical,
  Layers,
  Link2,
  MoreVertical,
  Pencil,
  Plus,
  Search,
  SearchX,
  Trash2,
  X,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useArchiveZakazka,
  useDeleteZakazka,
  useRestoreZakazka,
  useUpdateZakazka,
  useZakazkyList,
  useZakazkyStavPocty,
} from "@/api/hooks/useZakazky";
import { useKategorieZakaziek } from "@/api/hooks/useKategorieZakaziek";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { useDebounce } from "@/lib/useDebounce";
import { formatDateTime, formatEur, formatUpravene } from "@/lib/format";
import {
  BEZ_KATEGORIE,
  filtreDoAdresy,
  filtreZAdresy,
  kategoriaApi,
  PREDVOLENE_FILTRE,
  triedenieApi,
  zabudniZakazku,
  zapamatajFiltre,
  type FiltreZoznamu,
  type PoleTriedenia,
  type ZalozkaZoznamu,
} from "@/lib/zoznamZakaziek";
import { StavBadge } from "@/components/zakazka/StavBadge";
import {
  KategorieMobil,
  KategoriePanel,
  ZAKAZKA_MIME,
} from "@/components/zakazka/KategoriePanel";
import { MenuPolozka, MenuStitok } from "@/components/zakazka/MenuPolozka";
import { useImport } from "@/lib/import/ImportContext";
import { ZakazkaCreateDialog } from "@/components/zakazka/ZakazkaCreateDialog";
import { ZakazkaEditDialog } from "@/components/zakazka/ZakazkaEditDialog";
import {
  NAZOV_PRIPOJENIA,
  POPIS_PRIPOJENIA,
  PripojitDialog,
  stranyPripojenia,
  type StranaPripojenia,
} from "@/components/zakazka/PripojitDialog";
import { cn } from "@/lib/utils";
import type { KategoriaZakazky, StavZakazky, ZakazkaListItem } from "@/api/schemas";

/** Stavy v ľavom menu — klik filtruje (archív je samostatná položka dole). */
const STAVY: {
  kluc: Exclude<ZalozkaZoznamu, "archiv">;
  nazov: string;
  ikona: LucideIcon;
}[] = [
  { kluc: "vsetky", nazov: "Všetky", ikona: Layers },
  { kluc: "ponuka", nazov: "Ponuky", ikona: FileText },
  // Prijatá zákazka, ktorá ešte nejde na pílu (KOLO 204)
  { kluc: "v_priprave", nazov: "V príprave", ikona: ClipboardList },
  { kluc: "vo_vyrobe", nazov: "Vo výrobe", ikona: Factory },
  { kluc: "dokoncena", nazov: "Dokončené", ikona: CircleCheck },
];

const NA_STRANU = 50;

/** „1 zákazka", „3 zákazky", „5 zákaziek". */
export function pocetZakaziek(n: number): string {
  if (n === 1) return "1 zákazka";
  if (n >= 2 && n <= 4) return `${n} zákazky`;
  return `${n} zákaziek`;
}

/**
 * Prehľad zákaziek (KOLO 171) — hľadanie a tabuľka, ktorá sa na mobile
 * skladá do kariet (ten istý DOM, len iné CSS).
 *
 * Filter (stav, hľadanie, triedenie, strana) je v adrese
 * (`lib/zoznamZakaziek.ts`), takže ho drží šípka Späť a návrat zo zákazky.
 *
 * KOLO 172: kategórie (Dr.Max, PMS…) — filter navyše k stavu; riadok sa
 * dá potiahnuť na kategóriu alebo presunúť cez ⋮.
 *
 * KOLO 174 („menu 1" z plátna variantov): stav aj kategórie sú vľavo
 * v jednom menu s počtami, ktoré sa navzájom prepočítavajú; dlaždice
 * odišli. Nová zákazka a Import sú v riadku nad tabuľkou, nie v hlavičke.
 * Tabuľka má samostatný stĺpec Číslo a stĺpec Kategória len pri
 * „Všetky kategórie".
 */
export function ZakazkyListPage(): JSX.Element {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  // Sprievodca importu je od KOLA 117 v `AppShell` — odtiaľto sa len
  // otvára. Keby visel na tejto stránke, odchod na zákazku by ho
  // odmountoval a minimalizovanie by nemalo čo prežiť.
  const { otvorImport, otvorKoncept } = useImport();
  const [editId, setEditId] = useState<string | null>(null);
  // Pripojenie výroby k výpočtu (KOLO 208), spojenie výrob (KOLO 209) —
  // jedno okno pre celý zoznam
  const [pripojit, setPripojit] = useState<{
    zakazka: ZakazkaListItem;
    strana: StranaPripojenia;
  } | null>(null);

  const [params, setParams] = useSearchParams();
  const filtre = useMemo(() => filtreZAdresy(params), [params]);

  // Odkaz z programu Tandem Zostavy: `/zakazky?koncept=<id>` otvorí ten
  // rozrobený import (KOLO 127). Parameter sa z adresy hneď zmaže — inak
  // by sa okno otváralo znova pri každom návrate na zoznam a pri F5.
  const konceptZOdkazu = params.get("koncept");
  useEffect(() => {
    if (!konceptZOdkazu) return;
    otvorKoncept(konceptZOdkazu);
    const zvysok = new URLSearchParams(params);
    zvysok.delete("koncept");
    setParams(zvysok, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [konceptZOdkazu]);

  // Šípka v hlavičke zákazky a menu Zákazky vedú späť na tento filter
  useEffect(() => {
    zapamatajFiltre(filtre);
  }, [filtre]);

  // Odchod zo zoznamu (KOLO 215) — Zákazky v menu sa vrátia sem, nie do
  // zákazky otvorenej pred ním
  useEffect(() => {
    zabudniZakazku();
  }, []);

  function zmenFiltre(zmena: Partial<FiltreZoznamu>): void {
    setParams(
      (prev) => filtreDoAdresy({ ...filtreZAdresy(prev), ...zmena }, prev),
      { replace: true },
    );
  }

  // Pole hľadania píše hneď, do adresy (a na server) ide po 300 ms ticha
  const [hladat, setHladat] = useState(filtre.hladat);
  const hladatTicho = useDebounce(hladat, 300);
  useEffect(() => {
    if (hladatTicho !== filtre.hladat) zmenFiltre({ hladat: hladatTicho, strana: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hladatTicho]);
  useEffect(() => {
    // Adresa zmenená zvonka (Zrušiť filtre) prepíše pole; vlastný zápis
    // nie — pole medzitým mohlo dostať ďalšie písmená
    if (filtre.hladat !== hladatTicho) setHladat(filtre.hladat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtre.hladat]);

  const archivView = filtre.zalozka === "archiv";
  const stavFiltra: StavZakazky | null =
    filtre.zalozka === "vsetky" || filtre.zalozka === "archiv" ? null : filtre.zalozka;
  const zakazkyQuery = useZakazkyList({
    page: filtre.strana,
    per_page: NA_STRANU,
    sort: triedenieApi(filtre),
    search: filtre.hladat.trim() || undefined,
    stav: stavFiltra ?? undefined,
    archiv: archivView || undefined,
    ...kategoriaApi(filtre),
  });
  const pocty = useZakazkyStavPocty(kategoriaApi(filtre)).data?.data;
  // Počty kategórií v rámci zvoleného stavu — rovnaký dopyt ako v menu
  const kategorieQuery = useKategorieZakaziek(archivView, stavFiltra);
  const kategorie = useMemo(
    () =>
      [...(kategorieQuery.data?.data.kategorie ?? [])].sort((a, b) =>
        a.nazov.localeCompare(b.nazov, "sk"),
      ),
    [kategorieQuery.data],
  );
  const nazvyKategorii = useMemo(
    () => new Map(kategorie.map((k) => [k.id, k.nazov])),
    [kategorie],
  );
  const vybranaKategoria =
    filtre.kategoria && filtre.kategoria !== BEZ_KATEGORIE ? filtre.kategoria : null;

  // Kategória z adresy, ktorá už neexistuje (zmazaná, starý odkaz) → Všetky
  useEffect(() => {
    if (vybranaKategoria && kategorieQuery.isSuccess && !nazvyKategorii.has(vybranaKategoria)) {
      zmenFiltre({ kategoria: null, strana: 1 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vybranaKategoria, kategorieQuery.isSuccess, nazvyKategorii]);

  const presunMutation = useUpdateZakazka();
  async function presunDoKategorie(zakazkaId: string, kategoriaId: string | null): Promise<void> {
    try {
      await presunMutation.mutateAsync({ id: zakazkaId, data: { kategoria_id: kategoriaId } });
      toast({
        title: kategoriaId
          ? `Presunuté do ${nazvyKategorii.get(kategoriaId) ?? "kategórie"}`
          : "Presunuté medzi zákazky bez kategórie",
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Presun zlyhal",
        description: extractErrorMessage(err),
      });
    }
  }

  const items = zakazkyQuery.data?.data ?? [];
  const pagination = zakazkyQuery.data?.pagination;

  // Strana za koncom (zapamätaný filter, zmazaná posledná zákazka strany)
  // by ukázala „Žiadne zákazky“, hoci nejaké sú — skoč na poslednú.
  // Počas načítania sú v `data` riadky predošlého filtra (KOLO 225) —
  // podľa ich strán sa neskáče.
  const naozajNacitane = !zakazkyQuery.isPlaceholderData;
  useEffect(() => {
    if (
      naozajNacitane &&
      pagination &&
      pagination.total > 0 &&
      items.length === 0 &&
      filtre.strana > 1
    ) {
      zmenFiltre({ strana: Math.max(1, pagination.total_pages) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [naozajNacitane, pagination, items.length, filtre.strana]);

  function triedit(pole: PoleTriedenia): void {
    zmenFiltre(
      filtre.triedit === pole
        ? { zostupne: !filtre.zostupne, strana: 1 }
        : { triedit: pole, zostupne: true, strana: 1 },
    );
  }

  function zrusitFiltre(): void {
    setHladat("");
    setParams((prev) => filtreDoAdresy(PREDVOLENE_FILTRE, prev), { replace: true });
  }

  const nazovKategorie =
    filtre.kategoria === null
      ? null
      : filtre.kategoria === BEZ_KATEGORIE
        ? "Bez kategórie"
        : (nazvyKategorii.get(filtre.kategoria) ?? null);
  const nazovStavu = STAVY.find((s) => s.kluc !== "vsetky" && s.kluc === filtre.zalozka)?.nazov;
  const nadpis = archivView
    ? ["Archív", nazovKategorie].filter(Boolean).join(" · ")
    : [nazovKategorie ?? "Zákazky", nazovStavu].filter(Boolean).join(" · ");
  // V konkrétnej kategórii je jej názov v každom riadku zbytočný
  const ukazKategoriu = filtre.kategoria === null;
  const stlpcov = ukazKategoriu ? 8 : 7;
  const vyberStav = (zalozka: ZalozkaZoznamu): void => zmenFiltre({ zalozka, strana: 1 });
  const vyberKategoriu = (kategoria: string | null): void =>
    zmenFiltre({ kategoria, strana: 1 });

  return (
    <div className="grid gap-5 md:grid-cols-[232px_minmax(0,1fr)] md:gap-6">
      <aside
        aria-label="Filtre zákaziek"
        className="hidden space-y-5 border-r pr-3 md:sticky md:top-0 md:block md:self-start"
      >
        <StavMenu zalozka={filtre.zalozka} pocty={pocty} onVyber={vyberStav} />
        <KategoriePanel
          vybrana={filtre.kategoria}
          archiv={archivView}
          stav={stavFiltra}
          onVyber={vyberKategoriu}
          onPresun={(zakazkaId, kategoriaId) => void presunDoKategorie(zakazkaId, kategoriaId)}
        />
        <div className="border-t pt-3">
          <MenuPolozka
            ikona={Archive}
            nazov="Archív"
            pocet={pocty?.archiv}
            aktivna={archivView}
            oznacenie="pressed"
            title={archivView ? "Späť na zákazky" : "Zobraziť archivované zákazky"}
            onClick={() => vyberStav(archivView ? "vsetky" : "archiv")}
          />
        </div>
      </aside>

      <div className="min-w-0 space-y-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <div className="flex min-w-0 items-baseline gap-3">
          <h1 className="truncate text-2xl">{nadpis}</h1>
          {pagination ? (
            <span className="shrink-0 font-mono text-sm text-muted-foreground">
              {pocetZakaziek(pagination.total)}
            </span>
          ) : null}
        </div>
        <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
          <div className="relative w-full sm:w-auto sm:min-w-[12rem] sm:max-w-xs sm:flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Hľadať číslo alebo názov…"
              value={hladat}
              onChange={(e) => setHladat(e.target.value)}
              className="pl-9 pr-9"
              aria-label="Vyhľadať zákazku"
            />
            {hladat ? (
              <button
                type="button"
                onClick={() => {
                  setHladat("");
                  zmenFiltre({ hladat: "", strana: 1 });
                }}
                aria-label="Zrušiť hľadanie"
                title="Zrušiť hľadanie"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
          <span aria-hidden className="mx-1 hidden h-6 w-px bg-border sm:block" />
          {/* Import z TopSolidu zakladá zákazku rovnako ako „Nová" — oba
              patria nad tabuľku, nie do hlavičky (KOLO 174) */}
          <Button
            variant="outline"
            onClick={otvorImport}
            aria-label="Import z TopSolidu"
          >
            <FileUp className="h-4 w-4" />
            <span className="sm:hidden">Import</span>
            <span className="hidden sm:inline">Import z TopSolidu</span>
          </Button>
          <Button onClick={() => setDialogOpen(true)} aria-label="Nová zákazka">
            <Plus className="h-4 w-4" />
            Nová zákazka
          </Button>
        </div>
      </div>

      {/* Mobil — namiesto menu dve rozbaľovačky */}
      <div className="flex flex-col gap-2 md:hidden">
        <StavMobil zalozka={filtre.zalozka} pocty={pocty} onVyber={vyberStav} />
        <KategorieMobil
          vybrana={filtre.kategoria}
          archiv={archivView}
          stav={stavFiltra}
          onVyber={vyberKategoriu}
        />
      </div>

      <div className="overflow-hidden rounded-lg border bg-card">
        <Table className="block md:table">
          <TableHeader className="hidden md:table-header-group">
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-28">
                <TriedenieTlacidlo
                  pole="cislo_zakazky"
                  nazov="Číslo"
                  filtre={filtre}
                  onTriedit={triedit}
                />
              </TableHead>
              <TableHead>
                <TriedenieTlacidlo pole="nazov" nazov="Názov" filtre={filtre} onTriedit={triedit} />
              </TableHead>
              {ukazKategoriu ? <TableHead className="w-40">Kategória</TableHead> : null}
              <TableHead>
                <TriedenieTlacidlo pole="stav" nazov="Stav" filtre={filtre} onTriedit={triedit} />
              </TableHead>
              <TableHead className="text-center">Faktúra</TableHead>
              <TableHead className="text-right">Cena s DPH</TableHead>
              <TableHead>
                <TriedenieTlacidlo
                  pole="updated_at"
                  nazov="Upravené"
                  filtre={filtre}
                  onTriedit={triedit}
                />
              </TableHead>
              <TableHead className="w-12">
                <span className="sr-only">Akcie</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody
            aria-busy={zakazkyQuery.isPlaceholderData || undefined}
            className={cn(
              "block md:table-row-group",
              // Staré riadky, kým prídu nové (KOLO 225)
              zakazkyQuery.isPlaceholderData && "opacity-60 transition-opacity",
            )}
          >
            {zakazkyQuery.isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i} className="block px-4 py-3 md:table-row md:p-0">
                  {Array.from({ length: stlpcov }).map((__, j) => (
                    <TableCell key={j} className="block px-0 py-1 md:table-cell md:p-3">
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : zakazkyQuery.isError ? (
              <TableRow className="block md:table-row">
                <TableCell
                  colSpan={stlpcov}
                  className="block py-8 text-center text-destructive md:table-cell"
                >
                  Chyba načítania: {zakazkyQuery.error?.message}
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow className="block hover:bg-transparent md:table-row">
                <TableCell colSpan={stlpcov} className="block md:table-cell">
                  <EmptyState
                    druh={
                      archivView && !filtre.hladat.trim()
                        ? "archiv"
                        : filtre.kategoria && !filtre.hladat.trim() && filtre.zalozka === "vsetky"
                          ? "kategoria"
                          : filtre.hladat.trim() || filtre.zalozka !== "vsetky" || filtre.kategoria
                            ? "nenajdene"
                            : "ziadne"
                    }
                    onCreate={() => setDialogOpen(true)}
                    onZrusitFiltre={zrusitFiltre}
                  />
                </TableCell>
              </TableRow>
            ) : (
              items.map((z) => (
                <ZakazkaRiadok
                  key={z.id}
                  zakazka={z}
                  archivView={archivView}
                  kategorie={kategorie}
                  ukazKategoriu={ukazKategoriu}
                  kategoriaNazov={
                    z.kategoria_id ? (nazvyKategorii.get(z.kategoria_id) ?? null) : null
                  }
                  onPresun={(kategoriaId) => void presunDoKategorie(z.id, kategoriaId)}
                  onOpen={() => navigate(`/zakazky/${z.id}`)}
                  onEdit={() => setEditId(z.id)}
                  onPripojit={(strana) => setPripojit({ zakazka: z, strana })}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {pagination && pagination.total > pagination.per_page ? (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
          <span>
            Zobrazené {(pagination.page - 1) * pagination.per_page + 1}–
            {Math.min(pagination.page * pagination.per_page, pagination.total)}{" "}
            zo {pagination.total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => zmenFiltre({ strana: Math.max(1, pagination.page - 1) })}
            >
              ‹ Predchádzajúca
            </Button>
            <span className="self-center font-mono">
              {pagination.page} / {pagination.total_pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page >= pagination.total_pages}
              onClick={() => zmenFiltre({ strana: pagination.page + 1 })}
            >
              Ďalšia ›
            </Button>
          </div>
        </div>
      ) : null}
      </div>

      <ZakazkaCreateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        kategoriaId={vybranaKategoria}
      />
      <ZakazkaEditDialog
        zakazkaId={editId}
        onOpenChange={(open) => {
          if (!open) setEditId(null);
        }}
      />
      <PripojitDialog
        zakazka={pripojit?.zakazka ?? null}
        strana={pripojit?.strana ?? "vyroba"}
        onOpenChange={(open) => {
          if (!open) setPripojit(null);
        }}
        onPripojene={(id) => navigate(`/zakazky/${id}`)}
      />
    </div>
  );
}

/**
 * Stav v ľavom menu (KOLO 174 — namiesto dlaždíc z KOLA 171). Počty sú
 * v rámci zvolenej kategórie (`/zakazky/stav-pocty`).
 */
function StavMenu({
  zalozka,
  pocty,
  onVyber,
}: {
  zalozka: ZalozkaZoznamu;
  pocty: Record<string, number> | undefined;
  onVyber: (zalozka: ZalozkaZoznamu) => void;
}): JSX.Element {
  return (
    <div role="group" aria-label="Stav zákaziek" className="space-y-1.5">
      <MenuStitok>Stav</MenuStitok>
      <div className="space-y-0.5">
        {STAVY.map((s) => (
          <MenuPolozka
            key={s.kluc}
            ikona={s.ikona}
            nazov={s.nazov}
            pocet={pocty?.[s.kluc]}
            aktivna={zalozka === s.kluc}
            oznacenie="pressed"
            title={`Zobraziť: ${s.nazov}`}
            onClick={() => onVyber(s.kluc)}
          />
        ))}
      </div>
    </div>
  );
}

/** Mobil — stav ako rozbaľovačka, archív je jej posledná možnosť. */
function StavMobil({
  zalozka,
  pocty,
  onVyber,
}: {
  zalozka: ZalozkaZoznamu;
  pocty: Record<string, number> | undefined;
  onVyber: (zalozka: ZalozkaZoznamu) => void;
}): JSX.Element {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <label htmlFor="stav-mobil" className="text-sm text-muted-foreground">
        Stav
      </label>
      <select
        id="stav-mobil"
        value={zalozka}
        onChange={(e) => onVyber(e.target.value as ZalozkaZoznamu)}
        className="h-9 min-w-0 flex-1 rounded-md border bg-background px-2 text-sm"
      >
        {STAVY.map((s) => (
          <option key={s.kluc} value={s.kluc}>
            {s.nazov} ({pocty?.[s.kluc] ?? 0})
          </option>
        ))}
        <option value="archiv">Archív ({pocty?.archiv ?? 0})</option>
      </select>
    </div>
  );
}

/**
 * Riadok zákazky. Na mobile je to karta (grid): názov | ⋮, číslo |
 * kategória, stav | faktúra, cena | upravené — názov, akcie, číslo
 * a kategória majú pevné miesto, ostatné bunky tečú za nimi.
 */
function ZakazkaRiadok({
  zakazka: z,
  archivView,
  kategorie,
  ukazKategoriu,
  kategoriaNazov,
  onPresun,
  onOpen,
  onEdit,
  onPripojit,
}: {
  zakazka: ZakazkaListItem;
  archivView: boolean;
  kategorie: KategoriaZakazky[];
  ukazKategoriu: boolean;
  kategoriaNazov: string | null;
  onPresun: (kategoriaId: string | null) => void;
  onOpen: () => void;
  onEdit: () => void;
  onPripojit?: (strana: StranaPripojenia) => void;
}): JSX.Element {
  const maCenu = z.cena_celkom_s_dph != null && Number(z.cena_celkom_s_dph) !== 0;
  return (
    <TableRow
      onClick={onOpen}
      // Ťahanie na kategóriu v ľavom paneli (KOLO 172); archivovaná zákazka
      // sa upravovať nedá, tak sa ani neťahá
      draggable={!archivView}
      onDragStart={(e) => {
        e.dataTransfer.setData(ZAKAZKA_MIME, z.id);
        e.dataTransfer.setData("text/plain", z.nazov ?? z.cislo_zakazky);
        e.dataTransfer.effectAllowed = "move";
      }}
      className={cn(
        "group cursor-pointer",
        "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 px-4 py-3",
        "md:table-row md:p-0",
      )}
    >
      <TableCell
        className={cn(
          "col-start-1 row-start-2 block p-0 font-mono text-xs text-muted-foreground",
          "md:table-cell md:p-3 md:text-sm md:font-semibold md:text-foreground",
          !ukazKategoriu && "col-span-2",
        )}
      >
        {z.cislo_zakazky}
      </TableCell>
      <NazovCell zakazka={z} tahatelna={!archivView} />
      {ukazKategoriu ? (
        <TableCell
          className="col-start-2 row-start-2 block p-0 text-right text-xs text-muted-foreground md:table-cell md:p-3 md:text-left md:text-sm"
          data-stlpec="kategoria"
        >
          {kategoriaNazov ?? "—"}
        </TableCell>
      ) : null}
      <TableCell className="block p-0 md:table-cell md:p-3">
        <StavBadge stav={z.stav} />
      </TableCell>
      <FakturaCell zakazka={z} />
      <TableCell className="block p-0 font-display font-semibold tabular-nums md:table-cell md:p-3 md:text-right">
        {maCenu ? (
          <>
            {/* Cena ponuky (KOLO 231) — rozpočet pred koeficientom
                a zľavou je pri myši, keď sa líši */}
            <span
              title={
                z.cena_rozpoctu_s_dph != null &&
                Number(z.cena_rozpoctu_s_dph) !== Number(z.cena_celkom_s_dph)
                  ? `Ponuka · rozpočet ${formatEur(z.cena_rozpoctu_s_dph)}`
                  : undefined
              }
            >
              {formatEur(z.cena_celkom_s_dph)}
            </span>
            {z.cena_z_vyroby ? (
              // Zákazka bez výpočtu — cena je výrobná kalkulácia (KOLO 208)
              <span
                className="block text-[11px] font-normal text-muted-foreground"
                title="Zákazka nemá výpočet — cena je súčet výroby"
              >
                výroba
              </span>
            ) : null}
          </>
        ) : (
          <span
            className="text-muted-foreground"
            title="Zákazka zatiaľ nemá ocenené dielce"
          >
            —
          </span>
        )}
      </TableCell>
      <TableCell
        className="block whitespace-nowrap p-0 text-right text-muted-foreground md:table-cell md:p-3 md:text-left"
        title={formatDateTime(z.updated_at)}
      >
        {formatUpravene(z.updated_at)}
      </TableCell>
      <AkcieCell
        zakazka={z}
        archivView={archivView}
        kategorie={kategorie}
        onPresun={onPresun}
        onEdit={onEdit}
        onPripojit={onPripojit}
      />
    </TableRow>
  );
}

/** Akcie zákazky v menu ⋮ — Upraviť + Archivovať + Zmazať, v archíve Obnoviť + Zmazať. */
function AkcieCell({
  zakazka,
  archivView,
  kategorie,
  onPresun,
  onEdit,
  onPripojit,
}: {
  zakazka: {
    id: string;
    cislo_zakazky: string;
    kategoria_id?: string | null;
    vypocet_cislo?: string | null;
    ma_vyrobu?: boolean;
  };
  archivView: boolean;
  kategorie: KategoriaZakazky[];
  onPresun: (kategoriaId: string | null) => void;
  onEdit: () => void;
  onPripojit?: (strana: StranaPripojenia) => void;
}): JSX.Element {
  const strany = stranyPripojenia(zakazka);
  const archiveMutation = useArchiveZakazka();
  const restoreMutation = useRestoreZakazka();
  const deleteMutation = useDeleteZakazka();

  async function handleArchive(): Promise<void> {
    try {
      await archiveMutation.mutateAsync(zakazka.id);
      toast({
        title: "Zákazka archivovaná",
        description: `${zakazka.cislo_zakazky} — nájdeš ju v Archíve.`,
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Archivácia zlyhala",
        description: extractErrorMessage(err),
      });
    }
  }

  async function handleRestore(): Promise<void> {
    try {
      await restoreMutation.mutateAsync(zakazka.id);
      toast({
        title: "Zákazka obnovená",
        description: `${zakazka.cislo_zakazky} je späť medzi zákazkami.`,
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Obnovenie zlyhalo",
        description: extractErrorMessage(err),
      });
    }
  }

  async function handleDelete(): Promise<void> {
    if (
      !window.confirm(
        `Natrvalo zmazať zákazku ${zakazka.cislo_zakazky} vrátane všetkých ` +
          "variant, zostáv a dielcov? Táto akcia sa nedá vrátiť.",
      )
    ) {
      return;
    }
    try {
      await deleteMutation.mutateAsync(zakazka.id);
      toast({
        title: "Zákazka zmazaná",
        description: zakazka.cislo_zakazky,
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Mazanie zlyhalo",
        description: extractErrorMessage(err),
      });
    }
  }

  const busy =
    archiveMutation.isPending ||
    restoreMutation.isPending ||
    deleteMutation.isPending;

  return (
    <TableCell
      // Mobil: vpravo hore v karte; klik do menu neotvára zákazku
      className="col-start-2 row-start-1 block self-start p-0 text-right md:table-cell md:w-12 md:p-3"
      onClick={(e) => e.stopPropagation()}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            disabled={busy}
            aria-label={`Akcie zákazky ${zakazka.cislo_zakazky}`}
            title="Akcie"
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {archivView ? (
            <DropdownMenuItem onSelect={() => void handleRestore()}>
              <ArchiveRestore className="h-4 w-4" />
              Obnoviť z archívu
            </DropdownMenuItem>
          ) : (
            <>
              <DropdownMenuItem onSelect={onEdit}>
                <Pencil className="h-4 w-4" />
                Upraviť údaje
              </DropdownMenuItem>
              {/* Presun do kategórie — na mobile jediná cesta (ťahať sa tam nedá) */}
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <FolderInput className="h-4 w-4" />
                  Presunúť do kategórie
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuItem
                    disabled={!zakazka.kategoria_id}
                    onSelect={() => onPresun(null)}
                  >
                    Bez kategórie
                  </DropdownMenuItem>
                  {kategorie.length ? <DropdownMenuSeparator /> : null}
                  {kategorie.map((k) => (
                    <DropdownMenuItem
                      key={k.id}
                      disabled={zakazka.kategoria_id === k.id}
                      onSelect={() => onPresun(k.id)}
                    >
                      {k.nazov}
                      {zakazka.kategoria_id === k.id ? (
                        <Check className="ml-auto h-3.5 w-3.5" />
                      ) : null}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              {onPripojit
                ? // Výpočet a výroba do jednej zákazky (KOLO 208), dve výroby (209)
                  strany.map((strana) => (
                    <DropdownMenuItem
                      key={strana}
                      onSelect={() => onPripojit(strana)}
                      title={POPIS_PRIPOJENIA[strana]}
                    >
                      <Link2 className="h-4 w-4" />
                      {NAZOV_PRIPOJENIA[strana]}
                    </DropdownMenuItem>
                  ))
                : null}
              <DropdownMenuItem onSelect={() => void handleArchive()}>
                <Archive className="h-4 w-4" />
                Archivovať
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => void handleDelete()}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
            Zmazať natrvalo
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </TableCell>
  );
}

/**
 * Názov — inline premenovanie ceruzkou priamo v zozname. Číslo zákazky má
 * od KOLA 174 vlastný stĺpec; pod názvom ostáva číslo výpočtu a výpočty
 * (len pri dvoch a viac — „A“ pri každej zákazke bol šum).
 */
function NazovCell({
  zakazka: z,
  tahatelna,
}: {
  zakazka: ZakazkaListItem;
  tahatelna: boolean;
}): JSX.Element {
  const updateMutation = useUpdateZakazka();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");

  function start(): void {
    setVal(z.nazov ?? "");
    setEditing(true);
  }

  async function save(): Promise<void> {
    try {
      await updateMutation.mutateAsync({
        id: z.id,
        data: { nazov: val.trim() || null },
      });
      toast({ title: "Názov uložený", description: val.trim() || "—" });
      setEditing(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba pri ukladaní názvu",
        description: extractErrorMessage(err),
      });
    }
  }

  if (editing) {
    return (
      <TableCell
        className="block p-0 md:table-cell md:p-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-1">
          <Input
            autoFocus
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void save();
              if (e.key === "Escape") setEditing(false);
            }}
            aria-label={`Názov zákazky ${z.id}`}
            className="h-7 text-sm"
          />
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 shrink-0"
            aria-label="Uložiť názov"
            title="Uložiť názov"
            onClick={() => void save()}
          >
            <Check className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 shrink-0"
            aria-label="Zrušiť úpravu názvu"
            title="Zrušiť úpravu"
            onClick={() => setEditing(false)}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </TableCell>
    );
  }

  const nazov = z.nazov?.trim();
  // Výroba po častiach (KOLO 203/208) — „2 časti" namiesto zoznamu výpočtov
  const casti = z.pocet_casti ?? 0;
  const vypocty = casti > 1 ? [] : (z.varianty_nazvy ?? []);
  const vypocet =
    z.vypocet_cislo && z.vypocet_cislo !== z.cislo_zakazky ? z.vypocet_cislo : null;
  // Tovar (KOLO 230) — cena v stĺpci je nábytok + aktívny tovar
  const tovar = z.aktivna_tovar_varianta_id ? (z.cena_tovaru_s_dph ?? null) : null;
  const predTovarom = Boolean(vypocet || vypocty.length > 1 || casti > 1);

  return (
    <TableCell className="relative col-start-1 row-start-1 block min-w-0 p-0 md:table-cell md:p-3">
      {tahatelna ? (
        // Úchyt len naznačuje, že riadok sa dá potiahnuť na kategóriu —
        // v okraji bunky, aby neposúval názov
        <GripVertical
          aria-hidden
          className="absolute left-0 top-[15px] hidden h-3 w-3 cursor-grab text-muted-foreground/60 opacity-0 transition-opacity group-hover:opacity-100 md:block"
        />
      ) : null}
      <div className="flex min-w-0 items-center gap-1.5">
        <Link
          to={`/zakazky/${z.id}`}
          // Naviguje odkaz, nie aj riadok — dvojitý zápis do histórie by
          // chcel dve stlačenia Späť, kým sa user vráti na zoznam
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "block min-w-0 truncate font-medium transition-colors group-hover:text-primary",
            !nazov && "font-mono",
          )}
        >
          {nazov || z.cislo_zakazky}
        </Link>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            start();
          }}
          aria-label={`Upraviť názov ${z.nazov ?? z.id}`}
          title="Upraviť názov zákazky"
          className="shrink-0 opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100"
        >
          <Pencil className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
        </button>
      </div>
      {predTovarom || tovar ? (
        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
          {vypocet ? (
            <span className="font-mono" title="Číslo výpočtu">
              {vypocet}
            </span>
          ) : null}
          {vypocet && (vypocty.length > 1 || casti > 1) ? <span aria-hidden>·</span> : null}
          {casti > 1 ? (
            <span title={`Časti zákazky: ${(z.varianty_nazvy ?? []).join(", ")} — cena je ich súčet`}>
              {casti} {casti < 5 ? "časti" : "častí"}
            </span>
          ) : vypocty.length > 1 ? (
            <span title="Výpočty zákazky">výpočty {vypocty.join(", ")}</span>
          ) : null}
          {tovar ? (
            <>
              {predTovarom ? <span aria-hidden>·</span> : null}
              <span title="Cena zákazky zahŕňa aktívny tovar (regály CRC)">
                + tovar {formatEur(tovar)}
              </span>
            </>
          ) : null}
        </div>
      ) : null}
    </TableCell>
  );
}

/** Checkbox „Faktúra" — kalkulácia vyfakturovaná (optimistický zápis). */
function FakturaCell({
  zakazka,
}: {
  zakazka: { id: string; fakturovana?: boolean; cislo_zakazky: string };
}): JSX.Element {
  const updateMutation = useUpdateZakazka();
  const [checked, setChecked] = useState(!!zakazka.fakturovana);

  useEffect(() => {
    setChecked(!!zakazka.fakturovana);
  }, [zakazka.fakturovana]);

  async function toggle(value: boolean): Promise<void> {
    setChecked(value);
    try {
      await updateMutation.mutateAsync({
        id: zakazka.id,
        data: { fakturovana: value },
      });
    } catch (err) {
      setChecked(!value);
      toast({
        variant: "destructive",
        title: "Chyba pri ukladaní",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <TableCell
      className="block p-0 md:table-cell md:p-3"
      onClick={(e) => e.stopPropagation()}
    >
      <label className="flex cursor-pointer items-center justify-end gap-2 md:justify-center">
        {/* Na mobile nie je hlavička stĺpca — štítok nesie pole samo */}
        <span className="text-xs text-muted-foreground md:hidden">Faktúra</span>
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => void toggle(e.target.checked)}
          aria-label={`Faktúra ${zakazka.cislo_zakazky}`}
          title="Kalkulácia vyfakturovaná"
          className="h-4 w-4 cursor-pointer accent-primary"
        />
      </label>
    </TableCell>
  );
}

function TriedenieTlacidlo({
  pole,
  nazov,
  filtre,
  onTriedit,
}: {
  pole: PoleTriedenia;
  nazov: string;
  filtre: FiltreZoznamu;
  onTriedit: (pole: PoleTriedenia) => void;
}): JSX.Element {
  const aktivne = filtre.triedit === pole;
  return (
    <button
      type="button"
      onClick={() => onTriedit(pole)}
      className={cn(
        "inline-flex items-center gap-1 text-xs font-medium uppercase tracking-wide",
        aktivne ? "text-foreground" : "text-muted-foreground hover:text-foreground",
      )}
      aria-label={`Triediť podľa ${nazov}`}
      title={`Triediť podľa: ${nazov}`}
    >
      {nazov}
      {aktivne ? (
        filtre.zostupne ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronUp className="h-3 w-3" />
        )
      ) : null}
    </button>
  );
}

function EmptyState({
  druh,
  onCreate,
  onZrusitFiltre,
}: {
  druh: "ziadne" | "nenajdene" | "archiv" | "kategoria";
  onCreate: () => void;
  onZrusitFiltre: () => void;
}): JSX.Element {
  const Ikona =
    druh === "nenajdene" ? SearchX : druh === "archiv" ? Archive : druh === "kategoria" ? FolderInput : FolderOpen;
  return (
    <div className="flex flex-col items-center gap-2 py-12 text-center">
      <span className="mb-1 flex h-12 w-12 items-center justify-center rounded-full bg-background-alt text-muted-foreground">
        <Ikona className="h-6 w-6" />
      </span>
      {druh === "ziadne" ? (
        <>
          <p className="text-lg font-medium">Žiadne zákazky</p>
          <p className="text-sm text-muted-foreground">
            Začni prvou cenovou ponukou.
          </p>
          <Button className="mt-2" onClick={onCreate}>
            <Plus className="h-4 w-4" />
            Nová zákazka
          </Button>
        </>
      ) : (
        <>
          <p className="text-lg font-medium">
            {druh === "archiv"
              ? "Archív je prázdny"
              : druh === "kategoria"
                ? "V kategórii nie sú zákazky"
                : "Nenájdené"}
          </p>
          <p className="text-sm text-muted-foreground">
            {druh === "archiv"
              ? "Sem idú archivované zákazky — dajú sa obnoviť."
              : druh === "kategoria"
                ? "Potiahni sem riadok zo Všetkých zákaziek alebo použi ⋮ → Presunúť do kategórie."
                : "Skús inú frázu alebo zruš filtre."}
          </p>
          <Button variant="outline" size="sm" className="mt-2" onClick={onZrusitFiltre}>
            {druh === "archiv"
              ? "Späť na zákazky"
              : druh === "kategoria"
                ? "Všetky zákazky"
                : "Zrušiť filtre"}
          </Button>
        </>
      )}
    </div>
  );
}
